import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  Cylinder as CylinderIcon,
  Download,
  Image as ImageIcon,
  Info,
  Loader2,
  Phone,
  Printer,
  RefreshCw,
  Wrench,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { fetchFunctionPdf, runPdf } from '@/lib/pdf';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import {
  SUPPORT_PHONE,
  SUPPORT_PHONE_TOLL_FREE,
  SUPPORT_TEL_HREF,
  SUPPORT_TOLL_FREE_HREF,
} from '@/lib/constants';
import { useCustomer } from '@/hooks/useCustomer';
import { useCustomerFeatures } from '@/hooks/useCustomerFeatures';
import { usePendingPayments, processingNote, type PendingPayment } from '@/hooks/usePendingPayments';
import { RecurringRentalModal } from '@/components/RecurringRentalModal';
import {
  useInvoiceDetail,
  useInvoiceLines,
  type InvoiceLine,
  type InvoiceRow,
} from '@/hooks/useInvoices';
import {
  useEquipmentAssets,
  useEquipmentContracts,
  type EquipmentAssetRow,
  type EquipmentContractRow,
} from '@/hooks/useEquipmentRentals';
import {
  useCylinderInventory,
  type CylinderInventoryRow,
} from '@/hooks/useCylinders';
import {
  useRefreshSfJobConfirmation,
  useSfJobConfirmation,
  type SfJobConfirmation,
} from '@/hooks/useSfJobConfirmation';
import { useAuditDocument } from '@/hooks/useAudit';
import { TankRentalAuditDoc } from '@/components/audit/TankRentalAuditDoc';
import { fmtMirrorDate } from '@/lib/dates';

function fmtDate(iso: string): string {
  return fmtMirrorDate(iso, { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Per-type invoice template. Branches the on-screen invoice "look" off the
 * rental classification already carried on the invoice row (derived from
 * `revenue_line` in `orders.v_invoices_all`):
 *   - equipment rental  → equipment template (asset/contract panel)
 *   - tank rental (CO₂)  → cylinder template (demurrage panel)
 *   - otherwise          → standard (current look, unchanged)
 *
 * Robustness: prefer equipment when an invoice carries both; fall back to
 * standard when `rentalKind` is null.
 */
type InvoiceTemplate = 'equipment' | 'cylinder' | 'standard';

function invoiceTemplate(invoice: InvoiceRow): InvoiceTemplate {
  if (invoice.rentalKind === 'equipment' || invoice.hasEquipmentRental) {
    return 'equipment';
  }
  if (invoice.rentalKind === 'tank' || invoice.hasTankRental) {
    return 'cylinder';
  }
  return 'standard';
}

function deliveryLines(
  invoice: InvoiceRow,
  fallback: { city: string | null; state: string | null } | null,
): string[] {
  const line1 = invoice.deliveryLine1?.trim() || null;
  const city = invoice.deliveryCity?.trim() || fallback?.city || null;
  const state = invoice.deliveryState?.trim() || fallback?.state || null;
  const postal = invoice.deliveryPostal?.trim() || null;
  const cityStateZip = [
    [city, state].filter(Boolean).join(', ') || null,
    postal,
  ]
    .filter(Boolean)
    .join(' ');
  return [line1, cityStateZip || null].filter(Boolean) as string[];
}

export default function InvoiceDetail() {
  const { invoice_id } = useParams<{ invoice_id: string }>();
  const { data: invoice, isLoading } = useInvoiceDetail(invoice_id);
  const { data: lines, isLoading: linesLoading } = useInvoiceLines(invoice_id);
  const { data: ctx } = useCustomer();
  const { data: features } = useCustomerFeatures();
  const { data: confirmation } = useSfJobConfirmation(invoice?.sfJobId ?? null);
  const { data: pendingPayments } = usePendingPayments();

  // CR-ADJ invoices are driver cylinder audits — load the Tank Rental Audit
  // document (counts + photos + signature) filed against this invoice.
  const isCylinderAudit = !!invoice?.docNumber?.toUpperCase().startsWith('CR-ADJ');
  // SYS-ADJ invoices are the SYSTEM's own correction to the cylinder books —
  // raised so a pickup that exceeded the recorded count could go through.
  // Nobody counted anything, so there is no audit document to load and it must
  // never be presented as one; the count still owes a real audit.
  const isSystemAdjustment = !!invoice?.docNumber
    ?.toUpperCase()
    .startsWith('SYS-ADJ');
  const { data: auditDoc } = useAuditDocument({
    invoiceId: isCylinderAudit ? invoice?.qboInvoiceId : null,
  });

  // Type-specific data for the rental templates. These read the same
  // customer-scoped mirror views the Equipment + Cylinders pages use; no new
  // data fetching is introduced. They run unconditionally (hooks rules) but the
  // panels only render for the matching template, and the hooks degrade to []
  // when the customer has nothing on file.
  const { data: equipmentAssets, isLoading: equipmentLoading } =
    useEquipmentAssets();
  const { data: equipmentContracts } = useEquipmentContracts();
  const { data: cylinderInventory, isLoading: cylindersLoading } =
    useCylinderInventory();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const docRef = useRef<HTMLDivElement>(null);

  // Download + Print both use the SERVER invoice PDF (customer-invoice-pdf) —
  // the exact document the DocuPost mailed invoice uses — so mailed, saved,
  // and printed invoices are identical. The on-screen document is view-only.
  async function fetchInvoicePdf(): Promise<Blob> {
    const qboId = invoice?.qboInvoiceId;
    if (!qboId) throw new Error('invoice not loaded');
    return fetchFunctionPdf(`customer-invoice-pdf?invoice_id=${encodeURIComponent(qboId)}`);
  }

  const pdfName = `brix-invoice-${invoice?.docNumber ?? invoice?.qboInvoiceId ?? 'invoice'}.pdf`;

  async function handleDownloadPdf() {
    setDownloading(true);
    try {
      await runPdf('download', fetchInvoicePdf, pdfName);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InvoiceDetail] PDF download failed:', err);
      window.alert(`We couldn't download this invoice. ${err instanceof Error ? err.message : ''}`);
    } finally {
      setDownloading(false);
    }
  }

  async function handlePrintPdf() {
    setPrinting(true);
    try {
      // runPdf opens the print tab BEFORE the fetch — see openPdfTab. If the
      // PDF can't be built, the message lands in that tab.
      await runPdf('print', fetchInvoicePdf, pdfName);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InvoiceDetail] PDF print failed:', err);
    } finally {
      setPrinting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-900">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading invoice…
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="h-full overflow-y-auto scrollbar-thin pb-14 md:pb-0">
        <div className="px-6 md:px-8 pt-6 max-w-3xl mx-auto">
          <BackToList />
          <div className="rounded-2xl border border-brix-border bg-brix-surface px-6 py-10 text-center shadow-card mt-4">
            <p className="text-sm text-slate-900">
              That invoice isn't on your account, or hasn't posted yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const primaryLocation = ctx?.locations[0] ?? null;
  const template = invoiceTemplate(invoice);
  const eyebrow =
    template === 'equipment'
      ? 'Equipment Rental'
      : template === 'cylinder'
        ? 'Gas Cylinder Rental'
        : null;

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-14 md:pb-0">
      <div data-print-doc className="px-6 md:px-8 pt-6 pb-10 max-w-3xl mx-auto">
        {/* Back + actions row (hidden in print) */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <BackToList />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleDownloadPdf()}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Download size={13} />
              )}
              Download PDF
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handlePrintPdf()}
              disabled={printing}
            >
              {printing ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Printer size={13} />
              )}
              Print
            </Button>
          </div>
        </div>

        {/* The invoice "document" — dark-navy framed, deeper shadow */}
        <div
          ref={docRef}
          className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep print:shadow-none print:border-0"
        >
          {/* Dark navy accent strip — doubled in thickness */}
          <div className="h-3 bg-brix-primary-deep" aria-hidden />

          {/* Logo band — INVOICE label sized to match the number for a clean
              Apple-style header. size="lg" (62px) is a tasteful letterhead
              masthead; "xl" (93px) rendered as a giant block in the PDF. */}
          <div className="px-6 md:px-8 pt-7 pb-6 border-b border-brix-border flex items-start justify-between gap-4">
            {/* Alameda Point Beverage Group lockup: large Brix logo · vertical
                rule · Alameda Soda. Inline (not BrixLogo) so the document brand
                matches the printed/mailed PDF exactly. */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-3">
                <img
                  src="/brand/brix-logo-full.png"
                  alt="Brix Beverage"
                  className="h-12 md:h-14 w-auto"
                />
                <span className="self-stretch w-px bg-brix-primary" aria-hidden />
                <img
                  src="/brand/alameda-soda-logo-navy.png"
                  alt="Alameda Soda"
                  className="h-7 md:h-8 w-auto"
                />
              </div>
              <p className="text-[10px] text-brix-muted">Alameda Point Beverage Group Inc.</p>
            </div>
            <div className="text-right">
              {eyebrow && (
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brix-primary leading-none mb-1.5">
                  {eyebrow}
                </p>
              )}
              <p className="font-display text-2xl font-bold tracking-tight text-slate-900 leading-none">
                Invoice
              </p>
              <p
                className="font-mono text-2xl font-bold text-slate-900 leading-none mt-1.5 tracking-tight"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                #{invoice.docNumber ?? invoice.qboInvoiceId}
              </p>
              <StatusBadge invoice={invoice} />
            </div>
          </div>

          {/* From / Billed to */}
          <div className="px-6 md:px-8 py-5 grid grid-cols-1 md:grid-cols-2 gap-5 border-b border-brix-border">
            <AddressBlock
              label="From"
              name="Brix Beverage"
              lines={[
                'Hangar 25',
                '1951 Monarch Street',
                'Alameda, CA 94501',
                `${SUPPORT_PHONE} · ${SUPPORT_PHONE_TOLL_FREE}`,
              ]}
            />
            <AddressBlock
              label="Delivery to"
              name={invoice.customerName}
              lines={deliveryLines(invoice, primaryLocation)}
            />
          </div>

          {/* Meta strip. The PO cell appears only when this invoice came from an
              order that carried one — an order placed by phone genuinely has no
              PO, and a "PO —" cell would read as something we lost rather than
              a field that never applied. */}
          <div
            className={cn(
              'px-6 md:px-8 py-5 grid grid-cols-2 gap-4 border-b border-brix-border bg-brix-surface-alt/40',
              invoice.poNumber ? 'md:grid-cols-5' : 'md:grid-cols-4',
            )}
          >
            <MetaCell label="Invoice date" value={fmtDate(invoice.txnDate)} />
            <MetaCell label="Due date" value={fmtDate(invoice.dueDate)} />
            {invoice.poNumber ? (
              <MetaCell label="Your PO number" value={invoice.poNumber} emphasis />
            ) : null}
            <MetaCell label="Terms" value={ctx?.customer.paymentTerms ?? '—'} />
            <MetaCell
              label="Balance due"
              value={formatUsd(invoice.balance)}
              emphasis
              tone={invoice.balance > 0 ? 'primary' : 'success'}
            />
          </div>

          {/* Order number (the SF job id — what Brix calls an "order" before
              it becomes an invoice). Hidden once the invoice is paid so
              the whole job-info section disappears together with the
              delivery card. */}
          {invoice.sfJobId && invoice.isOpen && (
            <div className="px-6 md:px-8 py-3 border-b border-brix-border bg-brix-surface-alt/20 flex items-center gap-2">
              <span className="text-[10px] tracking-tight text-slate-900 font-bold">
                Order number
              </span>
              <span
                className="text-sm font-mono font-bold text-slate-900 tracking-tight"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                #{invoice.sfJobId}
              </span>
            </div>
          )}

          {/* Line items */}
          <div className="px-6 md:px-8 py-5 border-b border-brix-border">
            <div className="text-[10px] tracking-tight text-slate-900 font-bold mb-2">
              Line items
            </div>
            <LineItems
              lines={lines ?? []}
              loading={linesLoading}
              memo={invoice.memo}
            />
          </div>

          {/* Type-specific panel — sits inside the framed document card so it
              flows with the on-screen document. (Download/Print use the server
              PDF, which renders its own equipment/cylinder section.) */}
          {template === 'equipment' && (
            <div className="px-6 md:px-8 py-5 border-b border-brix-border">
              <EquipmentPanel
                assets={equipmentAssets ?? []}
                contracts={equipmentContracts ?? []}
                loading={equipmentLoading}
              />
            </div>
          )}
          {template === 'cylinder' && !auditDoc && !isSystemAdjustment && (
            <div className="px-6 md:px-8 py-5 border-b border-brix-border">
              <CylinderPanel
                inventory={cylinderInventory ?? []}
                loading={cylindersLoading}
                invoiceDate={invoice.txnDate}
              />
            </div>
          )}

          {/* Tank Rental Audit — the merged audit document (counts, photos,
              driver signature) filed against this CR-ADJ adjustment invoice.
              Replaces the generic cylinder demurrage panel when present. */}
          {auditDoc && (
            <div className="px-6 md:px-8 py-5 border-b border-brix-border">
              <TankRentalAuditDoc audit={auditDoc} />
            </div>
          )}

          {/* System correction — a SYS-ADJ invoice is not a delivery, a pickup
              or an audit. It records that we corrected our own cylinder count
              so an order could be filled, and says a real count is still due. */}
          {isSystemAdjustment && (
            <div className="px-6 md:px-8 py-5 border-b border-brix-border">
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <Wrench size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-800 leading-relaxed">
                    <div className="font-semibold text-slate-900 mb-1">
                      Cylinder records corrected
                    </div>
                    <p>
                      A pickup came back with more cylinders than our records
                      showed at your location, so we corrected our own count to
                      match. This is a $0 adjustment — nothing is charged and
                      nothing was delivered or picked up on it.
                    </p>
                    <p className="mt-2">
                      A physical tank count is still due at this location. Our
                      driver will do one on a coming visit and you will get the
                      signed audit for it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="px-6 md:px-8 py-5 border-b border-brix-border">
            <div className="ml-auto max-w-xs space-y-1.5">
              <TotalsRow label="Subtotal" value={formatUsd(invoice.totalAmount)} />
              <TotalsRow
                label="Paid"
                value={formatUsd(invoice.totalAmount - invoice.balance)}
              />
              <div className="h-px bg-brix-border my-2" />
              <TotalsRow
                label="Balance due"
                value={formatUsd(invoice.balance)}
                emphasis
              />
            </div>
          </div>

          {/* Delivery confirmation entry — replaces the old inline frames. The
              full signature + photos live in a modal so the invoice document
              itself stays clean and printable. Once the invoice is paid we
              hide the entry (Storage gets purged after 7 days anyway). */}
          {invoice.sfJobId && invoice.isOpen && (
            <div className="px-6 md:px-8 pt-6 print:hidden">
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-brix-primary-deep text-white text-sm font-semibold tracking-tight shadow-card hover:bg-brix-primary transition-colors cursor-pointer"
              >
                <ImageIcon size={15} className="text-white" />
                To see a confirmation of delivery including pictures and
                signatures, click here
              </button>
            </div>
          )}

          {/* Pay — our own portal payment engine for the invoice (QBO hosted
              link is only a fallback); recurring for rentals. */}
          <div className="px-6 md:px-8 py-6 print:hidden">
            <PayBlock
              invoice={invoice}
              payOnline={features?.payOnline !== false}
              processing={pendingPayments?.get(invoice.qboInvoiceId) ?? null}
              onRecurring={() => setRecurringOpen(true)}
            />
          </div>

          {/* Footer */}
          <div className="px-6 md:px-8 py-4 bg-brix-surface-alt/60 border-t border-brix-border text-center">
            <p className="text-[11px] text-slate-900 tracking-tight">
              Brix Beverage · Hangar 25 · 1951 Monarch Street · Alameda, CA 94501
            </p>
            <p className="text-[11px] text-slate-900 tracking-tight mt-0.5">
              <a href={SUPPORT_TEL_HREF} className="text-brix-primary">
                {SUPPORT_PHONE}
              </a>{' '}
              ·{' '}
              <a href={SUPPORT_TOLL_FREE_HREF} className="text-brix-primary">
                {SUPPORT_PHONE_TOLL_FREE}
              </a>
            </p>
          </div>
        </div>
      </div>

      {recurringOpen && ctx && invoice.rentalKind && (
        <RecurringRentalModal
          customerId={ctx.customer.id}
          rentalKind={invoice.rentalKind}
          sourceInvoiceId={invoice.qboInvoiceId}
          amount={invoice.balance || invoice.totalAmount}
          alreadyOn={invoice.rentalAutopay}
          onClose={() => setRecurringOpen(false)}
        />
      )}

      {invoice.sfJobId && (
        <DeliveryConfirmationModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          jobId={invoice.sfJobId}
          confirmation={confirmation ?? null}
        />
      )}
    </div>
  );
}

function BackToList() {
  return (
    <Link
      to="/invoices"
      className="inline-flex items-center gap-1.5 text-sm text-slate-900 hover:text-slate-900 transition-colors cursor-pointer"
    >
      <ArrowLeft size={14} /> Back to invoices
    </Link>
  );
}

function StatusBadge({ invoice }: { invoice: InvoiceRow }) {
  if (!invoice.isOpen) {
    return (
      <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 text-xs font-semibold tracking-tight rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        <CheckCircle2 size={11} /> Paid
      </span>
    );
  }
  if ((invoice.daysPastDue ?? 0) > 0) {
    return (
      <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 text-xs font-semibold tracking-tight rounded-full bg-red-600 text-white border border-red-700">
        Past due · {invoice.daysPastDue} day{invoice.daysPastDue === 1 ? '' : 's'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 text-xs font-semibold tracking-tight rounded-full bg-red-50 text-red-600 border border-red-200">
      Unpaid
    </span>
  );
}

function AddressBlock({
  label,
  name,
  lines,
}: {
  label: string;
  name: string;
  lines: string[];
}) {
  return (
    <div>
      <div className="text-[10px] tracking-tight text-slate-900 font-bold">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-slate-900 tracking-tight">
        {name}
      </div>
      {lines.filter(Boolean).map((l) => (
        <div
          key={l}
          className="text-xs text-slate-900 leading-relaxed tracking-tight"
        >
          {l}
        </div>
      ))}
    </div>
  );
}

function MetaCell({
  label,
  value,
  emphasis,
  tone,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  tone?: 'primary' | 'success';
}) {
  const valueColor =
    tone === 'primary'
      ? 'text-brix-primary'
      : tone === 'success'
        ? 'text-emerald-700'
        : 'text-slate-900';
  return (
    <div>
      <div className="text-[10px] tracking-tight text-slate-900 font-bold">
        {label}
      </div>
      <div
        className={cn(
          'mt-1 font-mono font-bold tracking-tight',
          emphasis ? 'text-xl' : 'text-base',
          valueColor,
        )}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
    </div>
  );
}

function TotalsRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4',
        emphasis ? 'text-base' : 'text-sm',
      )}
    >
      <span
        className={cn(
          'tracking-tight text-slate-900',
          emphasis ? 'font-bold' : 'font-medium',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'font-mono tracking-tight text-slate-900',
          emphasis ? 'font-bold' : 'font-medium',
        )}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </span>
    </div>
  );
}


function PayBlock({
  invoice,
  payOnline = true,
  processing = null,
  onRecurring,
}: {
  invoice: InvoiceRow;
  payOnline?: boolean;
  processing?: PendingPayment | null;
  onRecurring: () => void;
}) {
  const navigate = useNavigate();

  // A payment we initiated is settling — show "Processing" so they don't pay twice.
  if (processing && invoice.isOpen) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-5">
        <div className="flex items-start gap-3">
          <Loader2 size={18} className="text-amber-700 mt-0.5 shrink-0 animate-spin" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {processing.bank ? 'Bank payment clearing — 3–5 business days' : 'Payment processing'}
            </p>
            <p className="text-xs text-amber-700/80 mt-0.5 leading-relaxed">
              {processingNote(processing.bank)} It will show as paid once it settles — no need to pay again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Rental kind on autopay AND already settled — the managed state.
  //
  // ⚠ THIS BRANCH IS FOR A PAID INVOICE ONLY. It used to catch open ones too,
  //   which told a customer "paid automatically each month — no action needed"
  //   about an invoice nothing had paid, and removed their only way to pay it.
  //   Las Palmas sat on $77.50 of rentals for weeks that way (2026-09-15):
  //   enrolling in autopay was the thing that made the invoice unpayable, and
  //   autopay had never charged anybody. An OPEN invoice now falls through to
  //   the ordinary pay block below, whatever autopay claims about it.
  //
  //   The rule: being enrolled is not the same as having been charged, and only
  //   a zero balance proves the second one.
  if (invoice.isRental && invoice.rentalAutopay && !invoice.isOpen) {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface-alt/60 px-5 py-5">
        <div className="flex items-start gap-3 mb-3">
          <RefreshCw size={18} className="text-brix-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-900">On automatic payment</p>
            <p className="text-xs text-slate-700 mt-0.5 leading-relaxed">
              This {invoice.rentalKind === 'equipment' ? 'equipment' : 'cylinder'} rental is paid
              automatically each month — no action needed.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRecurring}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-brix-border text-sm font-semibold text-slate-900 hover:bg-brix-surface transition-colors"
        >
          <CalendarClock size={15} /> Manage recurring payment
        </button>
      </div>
    );
  }

  // Paid (not on autopay): confirm paid; rentals still offer recurring setup for future invoices.
  if (!invoice.isOpen) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Paid in full</p>
            <p className="text-xs text-emerald-700/80 mt-0.5">Thanks for the prompt payment.</p>
          </div>
        </div>
        {invoice.isRental && (
          <button
            type="button"
            onClick={onRecurring}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-brix-primary/40 bg-brix-surface text-sm font-semibold text-brix-primary hover:bg-brix-primary-tint/60 transition-colors"
          >
            <CalendarClock size={15} /> Set future {invoice.rentalKind === 'equipment' ? 'equipment' : 'cylinder'} rentals to auto-pay
          </button>
        )}
      </div>
    );
  }

  // Open & payable → hand off to our unified pay screen. Single-invoice and
  // multi-invoice pay both run through /pay + the pay-invoices function; the
  // QBO hosted link is intentionally NOT offered on the portal.
  const canPay = payOnline && invoice.balance > 0;
  // Enrolled in autopay and STILL open — autopay has not (yet) taken it, so the
  // customer gets the ordinary pay button plus an honest explanation.
  const onAutopayButOpen = invoice.isRental && invoice.rentalAutopay && invoice.isOpen;

  return (
    <div className="rounded-xl border border-brix-border bg-brix-primary-tint/40 px-5 py-5">
      <div className="flex items-start gap-3 mb-3">
        <CreditCard size={18} className="text-brix-primary mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-slate-900">Pay this invoice</p>
          <p className="text-xs text-slate-900 mt-0.5 leading-relaxed">
            {onAutopayButOpen
              ? 'This rental is set to pay automatically, but it has not been charged yet — you can pay it here now.'
              : 'Pay securely with card or bank — your balance updates automatically once it posts.'}
          </p>
        </div>
      </div>

      {canPay ? (
        <button
          type="button"
          onClick={() => navigate(`/pay?invoice=${encodeURIComponent(invoice.qboInvoiceId)}`)}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-emerald-600 text-white text-base font-semibold tracking-tight shadow-card hover:bg-emerald-700 transition-colors cursor-pointer"
        >
          <CreditCard size={16} /> Pay {formatUsd(invoice.balance)}
        </button>
      ) : (
        <button
          type="button"
          disabled
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-emerald-600/30 text-white/80 text-sm font-semibold tracking-tight cursor-not-allowed"
        >
          <CreditCard size={14} /> Online payment is being switched on
        </button>
      )}

      {invoice.isRental && (
        <button
          type="button"
          onClick={onRecurring}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-brix-primary/40 text-sm font-semibold text-brix-primary hover:bg-brix-primary-tint/60 transition-colors"
        >
          <CalendarClock size={15} />{' '}
          {onAutopayButOpen
            ? 'Manage recurring payment'
            : `Set this ${invoice.rentalKind === 'equipment' ? 'equipment' : 'cylinder'} rental for automatic payment`}
        </button>
      )}

      <p className="text-[11px] text-slate-900 text-center mt-3">
        Prefer a different method? Call{' '}
        <a href={SUPPORT_TEL_HREF} className="text-brix-primary inline-flex items-center gap-1">
          <Phone size={11} /> {SUPPORT_PHONE}
        </a>{' '}
        to pay over the phone or arrange a check.
      </p>
    </div>
  );
}

function LineItems({
  lines,
  loading,
  memo,
}: {
  lines: InvoiceLine[];
  loading: boolean;
  memo: string | null;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-brix-border px-4 py-5 bg-brix-surface-alt/40 flex items-center gap-2 text-xs text-slate-900">
        <Loader2 size={13} className="animate-spin" /> Loading line items…
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-brix-border px-4 py-5 bg-brix-surface-alt/40 flex items-start gap-3">
        <Info size={15} className="text-brix-primary mt-0.5 shrink-0" />
        <div className="text-xs text-slate-900 leading-relaxed">
          No line-item detail on file for this invoice. The header total
          below is the authoritative amount.
          {memo && (
            <>
              <br />
              <span className="text-slate-900">Memo:</span> {memo}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-brix-border overflow-hidden">
      <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_70px_90px_110px] gap-3 px-3 py-2.5 text-[11px] font-semibold tracking-tight text-white bg-brix-primary-deep">
        <div>Item</div>
        <div className="hidden sm:block text-right">Qty</div>
        <div className="hidden sm:block text-right">Unit</div>
        <div className="text-right">Amount</div>
      </div>
      {lines.map((l) => (
        <div
          key={l.id}
          className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_70px_90px_110px] gap-3 px-3 py-2.5 items-start text-sm border-b border-brix-border last:border-b-0"
        >
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 tracking-tight break-words">
              {l.itemName ?? l.description ?? '—'}
            </div>
            {l.itemName && l.description && l.description !== l.itemName && (
              <div className="text-xs text-slate-900 tracking-tight break-words">
                {l.description}
              </div>
            )}
            {/* Mobile: qty × unit folded under the item (the desktop columns are hidden). */}
            <div className="sm:hidden text-xs text-slate-500 font-mono mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {l.quantity != null ? l.quantity : '—'} × {l.unitPrice != null ? formatUsd(l.unitPrice) : '—'}
            </div>
          </div>
          <div
            className="hidden sm:block text-right font-mono text-slate-900 tracking-tight"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {l.quantity != null ? l.quantity : '—'}
          </div>
          <div
            className="hidden sm:block text-right font-mono text-slate-900 tracking-tight"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {l.unitPrice != null ? formatUsd(l.unitPrice) : '—'}
          </div>
          <div
            className="text-right font-mono font-semibold text-slate-900 tracking-tight whitespace-nowrap"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatUsd(l.amount)}
          </div>
        </div>
      ))}
      {memo && (
        <div className="px-3 py-2.5 text-xs text-slate-900 tracking-tight bg-brix-surface-alt/40 border-t border-brix-border">
          <span className="font-semibold">Memo:</span> {memo}
        </div>
      )}
    </div>
  );
}

/* --------------------------- equipment template --------------------------- */

function PanelHeading({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-brix-primary shrink-0">{icon}</span>
      <span className="text-[10px] tracking-tight text-slate-900 font-bold uppercase">
        {children}
      </span>
    </div>
  );
}

function EquipmentPanel({
  assets,
  contracts,
  loading,
}: {
  assets: EquipmentAssetRow[];
  contracts: EquipmentContractRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-brix-border px-4 py-5 bg-brix-surface-alt/40 flex items-center gap-2 text-xs text-slate-900">
        <Loader2 size={13} className="animate-spin" /> Loading equipment…
      </div>
    );
  }

  // Active assets first; drop anything already removed for a clean rental view.
  const liveAssets = assets.filter((a) => !a.removedAt);
  const liveContracts = contracts.filter(
    (c) => (c.status ?? '').toLowerCase() !== 'terminated',
  );

  if (liveAssets.length === 0 && liveContracts.length === 0) {
    return (
      <>
        <PanelHeading icon={<Wrench size={13} />}>
          Equipment on this contract
        </PanelHeading>
        <div className="rounded-lg border border-dashed border-brix-border px-4 py-5 bg-brix-surface-alt/40 flex items-start gap-3">
          <Info size={15} className="text-brix-primary mt-0.5 shrink-0" />
          <div className="text-xs text-slate-900 leading-relaxed">
            No equipment on file for your account. If you rent equipment from
            us and don't see it here, reach out and we'll get it listed.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PanelHeading icon={<Wrench size={13} />}>
        Equipment on this contract
      </PanelHeading>

      {liveAssets.length > 0 && (
        <div className="rounded-lg border border-brix-border overflow-hidden">
          <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.4fr_1fr_0.9fr_110px] gap-3 px-3 py-2.5 text-[11px] font-semibold tracking-tight text-white bg-brix-primary-deep">
            <div>Equipment</div>
            <div className="hidden sm:block">Serial #</div>
            <div className="hidden sm:block">Start / term</div>
            <div className="text-right">Monthly</div>
          </div>
          {liveAssets.map((a) => {
            const name =
              a.catalogName ||
              a.qboItemName ||
              [a.make, a.modelNumber].filter(Boolean).join(' ') ||
              a.description ||
              'Equipment';
            const makeModel = [a.make, a.modelNumber]
              .filter(Boolean)
              .join(' · ');
            const contract = a.contractId
              ? contracts.find((c) => c.id === a.contractId) ?? null
              : null;
            const term = [
              contract?.startDate ? fmtDate(contract.startDate) : null,
              contract?.endDate ? `– ${fmtDate(contract.endDate)}` : null,
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div
                key={a.id}
                className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.4fr_1fr_0.9fr_110px] gap-3 px-3 py-2.5 items-start text-sm border-b border-brix-border last:border-b-0"
              >
                <div className="flex items-start gap-2.5 min-w-0">
                  {/* Equipment photo — the same image the Equipment card shows. */}
                  {a.imageUrl && (
                    <img
                      src={a.imageUrl}
                      alt={name}
                      loading="lazy"
                      className="w-12 h-12 shrink-0 rounded-md border border-brix-border bg-white object-contain"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 tracking-tight break-words">
                      {name}
                    </div>
                    {makeModel && (
                      <div className="text-xs text-slate-500 tracking-tight break-words">
                        {makeModel}
                      </div>
                    )}
                    {a.assetTag && (
                      <div className="text-xs text-slate-500 tracking-tight">
                        Tag {a.assetTag}
                      </div>
                    )}
                    {a.isLoaner && (
                      <div className="text-xs text-brix-primary tracking-tight font-medium">
                        Loaner
                      </div>
                    )}
                    {/* Mobile: fold serial + term under the name. */}
                    <div className="sm:hidden text-xs text-slate-500 tracking-tight mt-0.5">
                      {a.serialNumber ? `S/N ${a.serialNumber}` : null}
                      {a.serialNumber && term ? ' · ' : null}
                      {term || null}
                    </div>
                  </div>
                </div>
                <div
                  className="hidden sm:block font-mono text-xs text-slate-900 tracking-tight break-words"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {a.serialNumber ?? '—'}
                </div>
                <div className="hidden sm:block text-xs text-slate-900 tracking-tight">
                  {term || '—'}
                </div>
                <div
                  className="text-right font-mono font-semibold text-slate-900 tracking-tight whitespace-nowrap"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {a.monthlyRent > 0 ? formatUsd(a.monthlyRent) : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Contracts without resolvable assets — show whatever we have. */}
      {liveAssets.length === 0 && liveContracts.length > 0 && (
        <div className="rounded-lg border border-brix-border overflow-hidden">
          <div className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2.5 text-[11px] font-semibold tracking-tight text-white bg-brix-primary-deep">
            <div>Contract</div>
            <div className="text-right">Monthly</div>
          </div>
          {liveContracts.map((c) => {
            const term = [
              c.startDate ? fmtDate(c.startDate) : null,
              c.endDate ? `– ${fmtDate(c.endDate)}` : null,
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div
                key={c.id}
                className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2.5 items-start text-sm border-b border-brix-border last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 tracking-tight break-words">
                    {c.contractNumber
                      ? `Contract ${c.contractNumber}`
                      : c.category ?? c.contractType ?? 'Equipment contract'}
                  </div>
                  {term && (
                    <div className="text-xs text-slate-500 tracking-tight">
                      {term}
                    </div>
                  )}
                </div>
                <div
                  className="text-right font-mono font-semibold text-slate-900 tracking-tight whitespace-nowrap"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {c.monthlyEquipmentAmount > 0
                    ? formatUsd(c.monthlyEquipmentAmount)
                    : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-slate-500 tracking-tight mt-2.5 leading-relaxed">
        Equipment listed reflects the assets currently assigned to your
        account. Monthly rates are billed for the period covered by this
        invoice.
      </p>
    </>
  );
}

/* --------------------------- cylinder template ---------------------------- */

function CylinderPanel({
  inventory,
  loading,
  invoiceDate,
}: {
  inventory: CylinderInventoryRow[];
  loading: boolean;
  invoiceDate: string;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-brix-border px-4 py-5 bg-brix-surface-alt/40 flex items-center gap-2 text-xs text-slate-900">
        <Loader2 size={13} className="animate-spin" /> Loading cylinders…
      </div>
    );
  }

  const rows = inventory.filter(
    (r) => r.currentBalance > 0 || r.deliveriesSince > 0 || r.pickupsSince > 0,
  );
  const totalTanks = rows.reduce((a, r) => a + r.currentBalance, 0);

  return (
    <>
      <PanelHeading icon={<CylinderIcon size={13} />}>
        Cylinders on rent
      </PanelHeading>

      {rows.length > 0 ? (
        <div className="rounded-lg border border-brix-border overflow-hidden">
          <div className="grid grid-cols-[1fr_70px_110px] gap-3 px-3 py-2.5 text-[11px] font-semibold tracking-tight text-white bg-brix-primary-deep">
            <div>Cylinder</div>
            <div className="text-right">On rent</div>
            <div className="text-right">Monthly</div>
          </div>
          {rows.map((r) => (
            <div
              key={r.label}
              className="grid grid-cols-[1fr_70px_110px] gap-3 px-3 py-2.5 items-center text-sm border-b border-brix-border last:border-b-0"
            >
              <div className="font-semibold text-slate-900 tracking-tight break-words">
                {r.label}
              </div>
              <div
                className="text-right font-mono font-semibold text-slate-900 tracking-tight"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {r.currentBalance}
              </div>
              <div
                className="text-right font-mono font-semibold text-slate-900 tracking-tight whitespace-nowrap"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {r.estimatedCharge > 0 ? formatUsd(r.estimatedCharge) : '—'}
              </div>
            </div>
          ))}
          <div className="grid grid-cols-[1fr_70px_110px] gap-3 px-3 py-2.5 items-center text-sm bg-brix-surface-alt/40 border-t border-brix-border">
            <div className="font-bold text-slate-900 tracking-tight">
              Total tanks on rent
            </div>
            <div
              className="text-right font-mono font-bold text-slate-900 tracking-tight"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {totalTanks}
            </div>
            <div />
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-brix-border px-4 py-5 bg-brix-surface-alt/40 flex items-start gap-3">
          <Info size={15} className="text-brix-primary mt-0.5 shrink-0" />
          <div className="text-xs text-slate-900 leading-relaxed">
            No cylinders currently on file for your account. The rental on this
            invoice covers the cylinders assigned during the billing period.
          </div>
        </div>
      )}

      {/* Demurrage explanation — concise, invoice-appropriate version of the
          copy on the Cylinders page. */}
      <div className="mt-3 rounded-xl bg-brix-primary-tint/40 border border-brix-primary/20 px-4 py-3.5">
        <p className="text-xs text-slate-900 leading-relaxed">
          <span className="font-semibold">About this charge.</span> The monthly
          cylinder rental is a <strong>demurrage fee</strong> — not a profit
          line item. It funds the safety and upkeep of every tank you have on
          site: paint, valves, seals, fittings, and the DOT-mandated
          hydrostatic re-certification each cylinder must pass. It accrues for
          the rental period covered by this invoice ({fmtDate(invoiceDate)}),
          for as long as cylinders remain assigned to your account. Return any
          tanks you aren't using and the fee drops with them next month.
        </p>
        <p className="text-[11px] text-slate-500 tracking-tight mt-2">
          Full rental terms are on the{' '}
          <Link to="/cylinders" className="text-brix-primary font-semibold print:no-underline">
            Cylinder inventory
          </Link>{' '}
          page.
        </p>
      </div>
    </>
  );
}

function fmtTimestamp(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function RefreshButton({ jobId }: { jobId: string }) {
  const refresh = useRefreshSfJobConfirmation();
  const [feedback, setFeedback] = useState<string | null>(null);
  const onClick = () => {
    setFeedback(null);
    refresh.mutate(jobId, {
      onSuccess: (r) => {
        if (r.photoCount + (r.signaturePresent ? 1 : 0) > 0) {
          setFeedback(
            `Refreshed — ${r.photoCount} photo${r.photoCount === 1 ? '' : 's'}` +
              (r.signaturePresent ? ' + signature' : ''),
          );
        } else if (r.errors.length > 0) {
          setFeedback(`Couldn't refresh: ${r.errors[0]}`);
        } else {
          setFeedback('Nothing on this order yet.');
        }
      },
      onError: (err) => setFeedback(err.message),
    });
  };
  return (
    <div className="mt-3 flex items-center gap-3 print:hidden">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClick}
        disabled={refresh.isPending}
      >
        <RefreshCw size={13} className={refresh.isPending ? 'animate-spin' : ''} />
        {refresh.isPending ? 'Refreshing…' : 'Refresh confirmation'}
      </Button>
      {feedback && <span className="text-xs text-slate-900">{feedback}</span>}
    </div>
  );
}

function DeliveryConfirmationModal({
  open,
  onClose,
  jobId,
  confirmation,
}: {
  open: boolean;
  onClose: () => void;
  jobId: string;
  confirmation: SfJobConfirmation | null;
}) {
  const [enlarged, setEnlarged] = useState<{ url: string; alt: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (enlarged) setEnlarged(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, enlarged, onClose]);

  if (!open) return null;

  const photos = (confirmation?.photos ?? []).filter((p) => p.is_image);
  const completed = fmtTimestamp(confirmation?.completedAt ?? null);
  const hasAnything = !!(
    confirmation?.signatureUrl ||
    confirmation?.signerName ||
    photos.length > 0
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delivery-confirmation-heading"
      className="fixed inset-0 z-[110] flex items-start md:items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-card-deep overflow-hidden border-2 border-brix-primary-deep">
        <div className="h-2 bg-brix-primary-deep" aria-hidden />

        <div className="px-6 md:px-8 pt-6 pb-4 flex items-start justify-between gap-4 border-b border-brix-border">
          <div>
            <h2
              id="delivery-confirmation-heading"
              className="font-display text-2xl font-bold text-slate-900 tracking-tight"
            >
              Delivery confirmation
            </h2>
            <p
              className="font-mono text-sm font-semibold text-slate-900 tracking-tight mt-1"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              Order #{jobId}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-900 hover:bg-brix-surface-alt transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 md:px-8 py-6 space-y-6">
          {!hasAnything && (
            <div className="flex items-start gap-2.5 text-sm text-slate-900">
              <Info size={16} className="text-slate-900 mt-0.5 shrink-0" />
              <p className="leading-relaxed">
                Delivery signature + photos for this order will appear here
                once they're uploaded from the field. Use Refresh below to
                check for the latest.
              </p>
            </div>
          )}

          {(confirmation?.signatureUrl ||
            confirmation?.signerName ||
            completed) && (
            <section>
              <div className="text-[11px] tracking-tight text-slate-900 font-bold mb-3">
                Signature
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5 items-start">
                {confirmation?.signatureUrl ? (
                  <button
                    type="button"
                    onClick={() =>
                      setEnlarged({
                        url: confirmation.signatureUrl!,
                        alt: confirmation.signerName
                          ? `Signature of ${confirmation.signerName}`
                          : 'Customer signature',
                      })
                    }
                    className="block w-full md:w-44 h-32 rounded-lg border border-brix-border bg-white overflow-hidden shadow-card hover:shadow-card-hover transition-shadow cursor-zoom-in"
                  >
                    <img
                      src={confirmation.signatureUrl}
                      alt={
                        confirmation.signerName
                          ? `Signature of ${confirmation.signerName}`
                          : 'Customer signature'
                      }
                      className="w-full h-full object-contain bg-white"
                    />
                  </button>
                ) : (
                  <div className="w-full md:w-44 h-32 rounded-lg border border-dashed border-brix-border bg-brix-surface-alt/40 flex items-center justify-center text-xs text-slate-900">
                    — not on file
                  </div>
                )}
                <dl className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-2.5">
                  <dt className="text-[10px] tracking-tight text-slate-900 font-bold pt-1">
                    Signed by
                  </dt>
                  <dd className="text-base font-semibold text-slate-900">
                    {confirmation?.signerName ?? '— not provided'}
                  </dd>
                  {completed && (
                    <>
                      <dt className="text-[10px] tracking-tight text-slate-900 font-bold pt-1">
                        Delivered
                      </dt>
                      <dd className="text-sm text-slate-900 font-medium inline-flex items-center gap-1.5">
                        <CheckCircle2 size={14} className="text-emerald-700" />
                        {completed}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            </section>
          )}

          {photos.length > 0 && (
            <section>
              <div className="text-[11px] tracking-tight text-slate-900 font-bold mb-3">
                Proof of delivery · {photos.length}{' '}
                {photos.length === 1 ? 'photo' : 'photos'}
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((p, i) => (
                  <button
                    key={`${p.url}-${i}`}
                    type="button"
                    onClick={() =>
                      setEnlarged({ url: p.url, alt: p.caption ?? 'Delivery photo' })
                    }
                    className="group block aspect-square rounded-lg border border-brix-border bg-white overflow-hidden shadow-card hover:shadow-card-hover transition-shadow cursor-zoom-in"
                    title={p.caption ?? undefined}
                  >
                    <img
                      src={p.url}
                      alt={p.caption ?? 'Delivery photo'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="pt-2 border-t border-brix-border">
            <RefreshButton jobId={jobId} />
          </div>
        </div>
      </div>

      {enlarged && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center px-4 py-8"
          onClick={() => setEnlarged(null)}
        >
          <div aria-hidden className="absolute inset-0 bg-slate-900/90" />
          <div className="relative max-w-5xl max-h-full">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEnlarged(null);
              }}
              aria-label="Close enlarged image"
              className="absolute -top-3 -right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-slate-900 shadow-card-deep hover:bg-brix-surface-alt cursor-pointer"
            >
              <X size={18} />
            </button>
            <img
              src={enlarged.url}
              alt={enlarged.alt}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg bg-white shadow-card-deep"
            />
          </div>
        </div>
      )}
    </div>
  );
}
